import { useEffect, useRef, useState } from 'react';

export interface LessonAvailabilityParams {
  selectedDate: Date | undefined;
  selectedStart: string | undefined;
  selectedEnd: string | undefined;
  selectedInstructorId: number | undefined;
  lessonId?: number;
}

export interface CarAvailabilityParams {
  selectedDate: Date | undefined;
  selectedPickupTime: string | undefined;
  selectedCarId: number | undefined;
  scheduleId?: number;
  defaultCarId?: number | null;
  defaultPickupTime?: string | null;
}

type MutationFn<T = any> = (query: string) => Promise<T>;

export function useAvailabilityChecks(
  lessonParams: LessonAvailabilityParams,
  carParams: CarAvailabilityParams,
  checkLessonAvailability: MutationFn<{ success?: boolean; data?: any }>,
  checkCarAvailability: MutationFn<{ success?: boolean; data?: any }>,
  formatDateYmd: (d: Date) => string
) {
  const { defaultCarId, defaultPickupTime } = carParams;
  const [lessonAvailability, setLessonAvailability] = useState<any>(null);
  const [isCarAvailable, setIsCarAvailable] = useState<boolean | null>(null);
  const [showDuplicateBookingModal, setShowDuplicateBookingModal] =
    useState(false);
  const [
    showDuplicateVehicleBookingModal,
    setShowDuplicateVehicleBookingModal
  ] = useState(false);

  const lastAvailabilityParams = useRef<string>('');
  const lastCarAvailabilityParams = useRef<string>('');
  // Lesson availability
  useEffect(() => {
    const {
      selectedDate,
      selectedStart,
      selectedEnd,
      selectedInstructorId,
      lessonId
    } = lessonParams;
    if (
      !selectedDate ||
      !selectedStart ||
      !selectedEnd ||
      !selectedInstructorId
    ) {
      return;
    }

    const startTime = `${formatDateYmd(selectedDate)} ${selectedStart}`;
    const endTime = `${formatDateYmd(selectedDate)} ${selectedEnd}`;

    const params = new URLSearchParams({
      startTime,
      endTime,
      instructor_id: String(selectedInstructorId)
    });
    if (lessonId) params.append('schedule_id', String(lessonId));

    const queryString = params.toString();
    if (lastAvailabilityParams.current === queryString) return;
    lastAvailabilityParams.current = queryString;

    let cancelled = false;
    (async () => {
      try {
        const resp = await checkLessonAvailability(queryString);
        if (!cancelled) {
          setLessonAvailability(resp);
          if (resp?.success && (resp?.data as any)?.status) {
            setShowDuplicateBookingModal(true);
          }
        }
      } catch {
        if (!cancelled) setLessonAvailability(null);
      }
    })();

    return () => {
      cancelled = false;
    };
  }, [lessonParams, checkLessonAvailability, formatDateYmd]);

  // Car availability
  useEffect(() => {
    const {
      selectedDate,
      selectedPickupTime,
      selectedCarId,
      scheduleId,
      defaultCarId,
      defaultPickupTime
    } = carParams;

    if (!selectedDate || !selectedPickupTime || !selectedCarId) {
      return;
    }

    if (
      defaultCarId &&
      defaultCarId === selectedCarId &&
      defaultPickupTime &&
      defaultPickupTime === selectedPickupTime
    ) {
      return;
    }

    const params = new URLSearchParams({
      pickup_time: selectedPickupTime,
      start_date: formatDateYmd(selectedDate),
      end_date: formatDateYmd(selectedDate),
      car_id: String(selectedCarId)
    });
    if (scheduleId) params.append('schedule_id', String(scheduleId));

    const queryString = params.toString();
    if (lastCarAvailabilityParams.current === queryString) return;
    lastCarAvailabilityParams.current = queryString;

    let cancelled = false;
    (async () => {
      try {
        const resp = await checkCarAvailability(queryString);
        if (!cancelled) {
          setIsCarAvailable(resp?.success === true);
          if (resp?.success && (resp?.data as any)?.status) {
            setShowDuplicateVehicleBookingModal(true);
          }
        }
      } catch {
        if (!cancelled) setIsCarAvailable(null);
      }
    })();

    return () => {
      cancelled = false;
    };
  }, [
    carParams,
    checkCarAvailability,
    formatDateYmd,
    defaultCarId,
    defaultPickupTime
  ]);

  return {
    lessonAvailability,
    isCarAvailable,
    showDuplicateBookingModal,
    setShowDuplicateBookingModal,
    showDuplicateVehicleBookingModal,
    setShowDuplicateVehicleBookingModal
  };
}
